import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';

import FilterIcon from '@lt/components/icons-react/FilterOutlined';
import { useTranslation } from '@lt/localization';
import { getIsMobile } from 'src/store/view/selectors';
import { getShowFiltersModal } from 'checkout/store/selectors/view';
import { toggleFiltersPanelAction } from 'checkout/store/actions/view';

import {
  getSelectedFiltersCount,
  isInitialFlightsFetchingDoneSelector,
  useShowFilters,
} from '../../store/flights-filters';

const portalTarget = document.querySelector('.lt-react-app-root') as Element;

interface IShowFiltersButton {
  filtersCount: number;
  isMobile?: boolean;
  toggleFiltersPanel: () => void;
  areInitialFlightsFetchingDone: boolean;
  isPackageRouteInView: boolean;
  isModalShown: boolean;
}

const ShowFiltersButton: FC<IShowFiltersButton> = ({
  isMobile = false,
  filtersCount,
  toggleFiltersPanel,
  areInitialFlightsFetchingDone,
  isPackageRouteInView,
  isModalShown,
}) => {
  const { t } = useTranslation();
  const [showButton, setShowButton] = useState(false);
  const [showFilters] = useShowFilters();

  useEffect(
    () => setShowButton(Boolean(isPackageRouteInView && showFilters)),
    [isPackageRouteInView, showButton, showFilters],
  );

  const visible = Boolean(
    isMobile &&
      portalTarget &&
      areInitialFlightsFetchingDone &&
      showButton &&
      !isModalShown,
  );

  if (!visible) return null;

  return createPortal(
    <StyledFloatingPanel>
      <StyledButtonContainer>
        <StyledButton onClick={toggleFiltersPanel}>
          <StyledFilterIcon width={23} height={23} />
          <StyledText>{t('checkout.flight.filtersButton')}</StyledText>
          {Boolean(filtersCount) && (
            <StyledCounter>{filtersCount}</StyledCounter>
          )}
        </StyledButton>
      </StyledButtonContainer>
    </StyledFloatingPanel>,
    portalTarget,
  );
};

const StyledFloatingPanel = styled.div`
  display: flex;
  position: sticky;
  bottom: 20px;
  width: 100%;
  justify-content: center;
  z-index: 2;
  height: 46px;
`;

const StyledFilterIcon = styled(FilterIcon)`
  transform: scale(0.85);
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledText = styled.span`
  display: inline-block;
  font-size: 15px;
  text-align: center;
  color: #17181a;
  margin: 0 3px;
`;

const StyledButton = styled.button.attrs({ type: 'button' })`
  box-shadow: 0 5px 20px 0 rgb(21 21 21 / 50%);
  min-width: 200px;
  height: 46px;
  border: none;
  border-radius: 46px;
  background-color: rgba(255, 255, 255, 0.9);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCounter = styled.span`
  width: 19px;
  height: 19px;
  line-height: 19px;
  text-align: center;
  background-color: #007aff;
  color: #ffffff;
  border-radius: 50%;
`;

const mapStateToProps = (state) => ({
  filtersCount: getSelectedFiltersCount(state),
  isMobile: getIsMobile(state),
  isModalShown: getShowFiltersModal(state),
  areInitialFlightsFetchingDone: isInitialFlightsFetchingDoneSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  toggleFiltersPanel: () => dispatch(toggleFiltersPanelAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShowFiltersButton);
